import { useState } from "react";
import { C, Badge, Btn } from "../components/ui";
import { Search, Lock, Clock, ChevronRight, Star, BookOpen, Zap } from "lucide-react";
import { PROVIDER_CONFIGS, ConnectionWizard } from "./ConnectionWizard";

const CATEGORIES = ["All", "Communication", "Ticketing", "Incident Mgmt", "Email", "Custom", "ITSM", "Cloud"];

// All providers in catalog (includes ones without full configs — shown as Coming Soon)
const ALL_PROVIDERS = [
  ...Object.values(PROVIDER_CONFIGS),
  { id: "teams",     name: "Microsoft Teams", icon: "🏢", color: "#5059C9", category: "Communication", description: "Adaptive card alerts to Teams channels", estimatedSetup: "5–8 min", securityBadges: ["Microsoft OAuth 2.0", "Encrypted"], authMethods: ["oauth"], preferredAuth: "oauth" as const },
  { id: "servicenow",name: "ServiceNow",       icon: "⚙️", color: "#81B5A1", category: "ITSM",          description: "Enterprise ITSM incident creation",   estimatedSetup: "8–12 min",securityBadges: ["OAuth 2.0", "Encrypted"], authMethods: ["token"], preferredAuth: "token" as const },
  { id: "opsgenie",  name: "OpsGenie",         icon: "🛡️", color: "#FF6B2B", category: "Incident Mgmt", description: "Smart alert routing and on-call mgmt", estimatedSetup: "3–5 min", securityBadges: ["API Token", "Encrypted"], authMethods: ["token"], preferredAuth: "token" as const },
  { id: "monday",    name: "Monday.com",       icon: "📋", color: "#FF3D57", category: "Ticketing",     description: "Task and project management boards",   estimatedSetup: "5–7 min", securityBadges: ["OAuth 2.0", "Encrypted"], authMethods: ["oauth"], preferredAuth: "oauth" as const },
  { id: "clickup",   name: "ClickUp",          icon: "🖱️", color: "#7B68EE", category: "Ticketing",     description: "All-in-one project management platform",estimatedSetup: "4–6 min", securityBadges: ["OAuth 2.0", "Encrypted"], authMethods: ["oauth"], preferredAuth: "oauth" as const },
  { id: "azure",     name: "Azure DevOps",     icon: "🔷", color: "#0078D4", category: "ITSM",          description: "Work items, boards, and pipelines",    estimatedSetup: "6–10 min",securityBadges: ["Microsoft OAuth 2.0", "PAT"], authMethods: ["oauth", "token"], preferredAuth: "oauth" as const },
  { id: "gchat",     name: "Google Chat",      icon: "💬", color: "#1DA462", category: "Communication", description: "Webhook notifications to Google Chat",  estimatedSetup: "3–5 min", securityBadges: ["Webhook HMAC", "Encrypted"], authMethods: ["webhook"], preferredAuth: "webhook" as const },
  { id: "sendgrid",  name: "SendGrid",         icon: "📨", color: "#1A82E2", category: "Email",         description: "Transactional email with analytics",   estimatedSetup: "3–5 min", securityBadges: ["API Key", "Encrypted"], authMethods: ["token"], preferredAuth: "token" as const },
  { id: "aws",       name: "AWS",              icon: "☁️", color: "#FF9900", category: "Cloud",         description: "CloudWatch alerts and SNS integration", estimatedSetup: "8–12 min",securityBadges: ["IAM Role", "Encrypted", "Least-privilege"], authMethods: ["iam"], preferredAuth: "iam" as const },
  { id: "gcp",       name: "Google Cloud",     icon: "🌐", color: "#4285F4", category: "Cloud",         description: "Cloud Monitoring and Pub/Sub alerts",  estimatedSetup: "6–10 min",securityBadges: ["Service Account", "OAuth 2.0", "Encrypted"], authMethods: ["oauth", "service-account"], preferredAuth: "service-account" as const },
].filter((p, i, arr) => arr.findIndex((x) => x.id === p.id) === i); // deduplicate

const AUTH_LABELS: Record<string, string> = {
  oauth: "OAuth 2.0", token: "API Token", webhook: "Webhook", smtp: "SMTP",
  iam: "IAM Role", "service-account": "Service Account", app: "GitHub App", multi: "Multiple",
};

const AUTH_COLORS: Record<string, string> = {
  oauth: C.blue, token: C.amber, webhook: C.green, smtp: C.purple,
  iam: C.cyan, "service-account": C.cyan, app: C.purple, multi: C.blue,
};

const POPULAR_IDS = ["slack", "jira", "pagerduty", "github", "discord", "webhook"];

interface CatalogProps {
  connectedIds: string[];
  onConnect: (providerId: string) => void;
  onClose: () => void;
}

export function IntegrationCatalog({ connectedIds, onConnect, onClose }: CatalogProps) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [wizardProvider, setWizardProvider] = useState<typeof PROVIDER_CONFIGS[string] | null>(null);

  const filtered = ALL_PROVIDERS.filter((p) => {
    const matchCat = category === "All" || p.category === category;
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const popular = ALL_PROVIDERS.filter((p) => POPULAR_IDS.includes(p.id));

  const openWizard = (providerId: string) => {
    const cfg = PROVIDER_CONFIGS[providerId];
    if (cfg) {
      setWizardProvider(cfg);
    } else {
      // For providers without full config, use a generic one
      onConnect(providerId);
    }
  };

  if (wizardProvider) {
    return (
      <ConnectionWizard
        provider={wizardProvider}
        onClose={() => setWizardProvider(null)}
        onComplete={(name, label, env) => {
          onConnect(wizardProvider.id);
          setWizardProvider(null);
          onClose();
        }}
      />
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(7,13,26,0.88)", backdropFilter: "blur(12px)", animation: "slide-up 0.2s ease" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{
          background: C.surface,
          border: `1px solid ${C.borderBright}`,
          borderRadius: 22,
          boxShadow: `0 24px 80px rgba(0,0,0,0.6)`,
        }}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b shrink-0" style={{ borderColor: C.border }}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-base font-bold" style={{ color: C.text }}>Connect an Integration</h2>
              <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>
                Every connection goes through a secure, guided setup wizard. No integration becomes active until fully validated.
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 1l12 12M13 1L1 13" stroke={C.textSub} strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {/* Search */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl mb-3" style={{ background: C.surfaceHigh, border: `1px solid ${C.border}` }}>
            <Search size={13} style={{ color: C.textMuted }} />
            <input
              className="flex-1 bg-transparent outline-none text-sm"
              style={{ color: C.text }}
              placeholder="Search integrations…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Category tabs */}
          <div className="flex gap-1.5 flex-wrap">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className="px-3 py-1 rounded-xl text-xs transition-all"
                style={{
                  background: category === cat ? C.blueDim : C.surfaceHigh,
                  color: category === cat ? C.blue : C.textMuted,
                  border: `1px solid ${category === cat ? C.borderBright : C.border}`,
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-6 space-y-6">
          {/* Popular section */}
          {category === "All" && !search && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Star size={12} style={{ color: C.amber }} />
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: C.textMuted }}>Popular</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {popular.map((p) => (
                  <CatalogCard
                    key={p.id}
                    provider={p as typeof ALL_PROVIDERS[0]}
                    connected={connectedIds.includes(p.id)}
                    onConnect={() => openWizard(p.id)}
                    compact
                  />
                ))}
              </div>
            </div>
          )}

          {/* All results */}
          <div>
            {category !== "All" || search ? null : (
              <div className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: C.textMuted }}>
                All Integrations ({filtered.length})
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filtered.map((p) => (
                <CatalogCard
                  key={p.id}
                  provider={p as typeof ALL_PROVIDERS[0]}
                  connected={connectedIds.includes(p.id)}
                  onConnect={() => openWizard(p.id)}
                />
              ))}
            </div>
            {filtered.length === 0 && (
              <div className="text-center py-12 space-y-3">
                <Search size={20} style={{ color: C.textMuted, opacity: 0.4, margin: "0 auto" }} />
                <div className="text-sm" style={{ color: C.textMuted }}>No integrations matching &ldquo;{search}&rdquo;</div>
              </div>
            )}
          </div>
        </div>

        {/* Footer note */}
        <div className="px-6 py-3 border-t flex items-center gap-2" style={{ borderColor: C.border, background: `${C.bg}88` }}>
          <Lock size={10} style={{ color: C.green }} />
          <span className="text-[10px]" style={{ color: C.textMuted }}>
            All credentials are encrypted at rest with AES-256. No integration becomes active until authentication, validation, and testing all pass.
          </span>
        </div>
      </div>
    </div>
  );
}

function CatalogCard({ provider, connected, onConnect, compact }: {
  provider: { id: string; name: string; icon: string; color: string; category: string; description: string; estimatedSetup: string; securityBadges: string[]; authMethods: string[]; preferredAuth: string };
  connected: boolean;
  onConnect: () => void;
  compact?: boolean;
}) {
  const primaryAuth = provider.preferredAuth as string;

  return (
    <div
      className="flex flex-col gap-3 p-4 rounded-2xl transition-all duration-200 hover:border-opacity-60"
      style={{
        background: C.surfaceHigh,
        border: `1px solid ${connected ? provider.color + "30" : C.border}`,
        boxShadow: connected ? `0 0 10px ${provider.color}15` : "none",
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2.5">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center text-lg"
            style={{ background: `${provider.color}18`, border: `1px solid ${provider.color}25` }}
          >
            {provider.icon}
          </div>
          <div>
            <div className="text-xs font-semibold" style={{ color: C.text }}>{provider.name}</div>
            <div className="text-[9px]" style={{ color: C.textMuted }}>{provider.category}</div>
          </div>
        </div>
        {connected && (
          <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-lg" style={{ background: C.greenDim, border: "1px solid rgba(16,185,129,0.2)" }}>
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: C.green }} />
            <span className="text-[9px]" style={{ color: C.green }}>Connected</span>
          </div>
        )}
      </div>

      {/* Description */}
      {!compact && <div className="text-[10px] leading-relaxed" style={{ color: C.textMuted }}>{provider.description}</div>}

      {/* Auth + time */}
      {!compact && (
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px]"
            style={{ background: `${AUTH_COLORS[primaryAuth] ?? C.blue}18`, color: AUTH_COLORS[primaryAuth] ?? C.blue, border: `1px solid ${AUTH_COLORS[primaryAuth] ?? C.blue}30` }}
          >
            <Lock size={8} />
            {AUTH_LABELS[primaryAuth] ?? primaryAuth}
          </div>
          <div className="flex items-center gap-1 text-[9px]" style={{ color: C.textMuted }}>
            <ClockIcon size={8} style={{ color: C.textMuted }} />
            {provider.estimatedSetup}
          </div>
        </div>
      )}

      {/* Security badges (compact: show only first one) */}
      {!compact && (
        <div className="flex flex-wrap gap-1">
          {provider.securityBadges.slice(0, 2).map((b) => (
            <div key={b} className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px]" style={{ background: C.greenDim, color: C.green, border: "1px solid rgba(16,185,129,0.15)" }}>
              <Lock size={7} />{b}
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-1.5 mt-auto">
        <Btn
          variant={connected ? "secondary" : "primary"}
          size="xs"
          icon={connected ? <Zap size={10} /> : <ChevronRight size={10} />}
          onClick={onConnect}
          className="flex-1 justify-center"
        >
          {connected ? "Add Instance" : "Connect"}
        </Btn>
        {!compact && (
          <Btn variant="ghost" size="xs" icon={<BookOpen size={10} />}>Guide</Btn>
        )}
      </div>
    </div>
  );
}

function ClockIcon(props: { size: number }) {
  return <svg width={props.size} height={props.size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
}
